import React, { useState, useMemo } from 'react';
import { useApi } from '../hooks/useApi';
import { api } from '../api';
import { fmt, fmtDate } from '../utils';
import { Card, CardHeader, StatCard, StatGrid, Tag, SearchInput, Skeleton, PageWrap, PageHeader, Table, useIsMobile } from '../components/UI';
import '../styles/Receivables.css';

export default function Receivables({ sourceId, syncRunId }) {
  const { data, loading } = useApi(() => api.receivables(sourceId, syncRunId), [sourceId, syncRunId]);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();

  const rows = useMemo(() => {
    let r = data?.debtors || [];
    if (search) { const q = search.toLowerCase(); r = r.filter(x => x.name?.toLowerCase().includes(q)); }
    return r;
  }, [data, search]);

  const total     = data?.totalOutstanding ?? 0;
  const within30  = rows.reduce((s, r) => s + (parseFloat(r.within_30) || 0), 0);
  const days31_60 = rows.reduce((s, r) => s + (parseFloat(r.days_31_60) || 0), 0);
  const days61_90 = rows.reduce((s, r) => s + (parseFloat(r.days_61_90) || 0), 0);
  const above90   = rows.reduce((s, r) => s + (parseFloat(r.above_90) || 0), 0);

  return (
    <PageWrap>
      <PageHeader title="Receivables" sub="Sundry Debtors with outstanding balances (FIFO ageing)" />
      <StatGrid cols={isMobile ? 2 : 5}>
        <StatCard label="Total Outstanding" value={`₹${fmt(total)}`} accent="green" />
        <StatCard label="0–30 Days"   value={`₹${fmt(within30)}`} />
        <StatCard label="31–60 Days"  value={`₹${fmt(days31_60)}`} accent={days31_60 > 0 ? 'amber' : undefined} />
        <StatCard label="61–90 Days"  value={`₹${fmt(days61_90)}`} accent={days61_90 > 0 ? 'amber' : undefined} />
        <StatCard label="> 90 Days"   value={`₹${fmt(above90)}`}   accent={above90 > 0 ? 'red' : undefined} />
      </StatGrid>
      <Card>
        <CardHeader
          title={`${rows.length} Debtors`}
          right={<SearchInput value={search} onChange={setSearch} placeholder="Search party..." />}
        />
        {loading ? <Skeleton rows={8} /> : (
          <Table
            columns={[
              { label: '#', key: '_idx',
                render: r => <span className="rec-dim">{r._idx}</span> },
              { label: 'Party Name', key: 'name' },
              { label: 'GSTIN', key: 'gstin',
                render: r => r.gstin ? <Tag>{r.gstin}</Tag> : <span className="rec-dim">—</span> },
              { label: 'Total (₹)', key: 'total_outstanding', right: true, mono: true,
                render: r => <span className="rec-green-bold">{fmt(r.total_outstanding)}</span> },
              { label: '0–30d', key: 'within_30', right: true, mono: true,
                render: r => parseFloat(r.within_30) > 0
                  ? <span>{fmt(r.within_30)}</span>
                  : <span className="rec-dim">—</span> },
              { label: '31–60d', key: 'days_31_60', right: true, mono: true,
                render: r => parseFloat(r.days_31_60) > 0
                  ? <span className="rec-amber">{fmt(r.days_31_60)}</span>
                  : <span className="rec-dim">—</span> },
              { label: '61–90d', key: 'days_61_90', right: true, mono: true,
                render: r => parseFloat(r.days_61_90) > 0
                  ? <span className="rec-orange">{fmt(r.days_61_90)}</span>
                  : <span className="rec-dim">—</span> },
              { label: '> 90d', key: 'above_90', right: true, mono: true,
                render: r => parseFloat(r.above_90) > 0
                  ? <span className="rec-red">{fmt(r.above_90)}</span>
                  : <span className="rec-dim">—</span> },
              { label: 'Oldest Invoice', key: 'oldest_invoice_date', noWrap: true,
                render: r => r.oldest_invoice_date
                  ? <span className="rec-date">{fmtDate(r.oldest_invoice_date)}</span>
                  : <span className="rec-dim">—</span> },
            ]}
            rows={rows.map((r, i) => ({ ...r, _idx: i + 1 }))}
            emptyMsg="No outstanding receivables"
          />
        )}
      </Card>
    </PageWrap>
  );
}
